import moment from 'moment-timezone';

import type { I18nService } from '@core/i18n-legacy';
import { DataInterval, DatasetBase, ReportsEChartsOption } from '@core/reports-types';
import { timeSpan } from '@core/reports-util';
import { ValueFormatterOptions } from '@core/utils-legacy';

import { BAR_CHART_BAR_MAX_WIDTH } from '../chart-layout';
import { numberAxisLabelFormatter } from '../number-axis-label-formatter';
import { daily } from '../trend/daily';
import { monthly } from '../trend/monthly';
import { quarterly } from '../trend/quarterly';
import { weekly } from '../trend/weekly';

export const addTrendSeries =
  <Type extends 'bar' | 'line'>(dataset: DatasetBase) =>
  <
    T extends {
      series: { type: Type };
      dataset: ReportsEChartsOption['dataset'];
    },
  >(
    opts: T,
  ) => ({
    ...opts,
    series: dataset.dimensions.slice(1).map((dimension, index) => ({
      ...opts.series,
      id: dimension.id || dimension.name,
      name: `${dimension.id}`,
      seriesLayoutBy: <const>'column',
      encode: {
        x: dataset.getIndexDimension()?.id,
        y: index + 1,
      },
      emphasis: {
        focus: <const>'series',
      },
      barMaxWidth: BAR_CHART_BAR_MAX_WIDTH,
    })),
  });

export const addValueYAxis =
  (formatterOptions: ValueFormatterOptions, i18n: I18nService) =>
  <T>(opts: T) => ({
    ...opts,
    yAxis: {
      minInterval: 1,
      type: <const>'value',
      axisLabel: {
        formatter: numberAxisLabelFormatter(formatterOptions, i18n),
      },
    },
  });

const getDataTimeBounds = (data: DatasetBase) => {
  const raw = data.getRawData();
  if (Array.isArray(raw) && Array.isArray(raw[1])) {
    return {
      start: new Date(raw[1][0]?.name),
      end: new Date(raw[raw.length - 1][0]?.name),
    };
  }

  const now = new Date();

  return {
    start: now,
    end: now,
  };
};
export const addIntervalXAxis =
  (dataset: DatasetBase, interval?: DataInterval) =>
  <T>(opts: T) => {
    const { start, end } = getDataTimeBounds(dataset);
    // handle empty or single record
    if (isNaN(start.getTime())) {
      return {
        ...opts,
        xAxis: {
          type: <const>'time',
          splitNumber: isNaN(end.getTime()) ? 1 : 0,
        },
      };
    }

    const ts = timeSpan(moment(start), moment(end));
    const ticksCount = dataset.datasetSize.rows - 1;

    const result = (() => {
      switch (interval) {
        case DataInterval.Daily:
          return daily(ticksCount);
        case DataInterval.Weekly:
          return weekly(ticksCount, ts);
        case DataInterval.Monthly:
          return monthly(ticksCount);
        case DataInterval.Quarterly:
          return quarterly(ticksCount);

        default:
          throw new Error(`Unhandled interval ${interval}`);
      }
    })();

    return {
      ...opts,
      ...result,
    };
  };
